import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { publicDocumentRequests } from '../services/api';

const REQUEST_TYPE_LABELS = {
  form_b: 'Form B — Information Certificate',
  form_f: 'Form F — Certificate of Payment',
  specific_documents: 'Document Request',
};

const STAGES = [
  { key: 'pending_payment', label: 'Payment' },
  { key: 'paid', label: 'Awaiting fulfillment' },
  { key: 'fulfilled', label: 'Ready' },
];

function StageTracker({ status }) {
  if (status === 'declined' || status === 'cancelled') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center text-red-700 text-sm">
        This request was {status}.
      </div>
    );
  }
  const idx = STAGES.findIndex((s) => s.key === status);
  return (
    <div className="flex items-center justify-between mb-6">
      {STAGES.map((s, i) => (
        <React.Fragment key={s.key}>
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
              i <= idx ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'}`}>
              {i < idx ? '✓' : i + 1}
            </div>
            <span className={`text-xs mt-1.5 text-center ${i <= idx ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{s.label}</span>
          </div>
          {i < STAGES.length - 1 && (
            <div className={`flex-1 h-0.5 mx-2 ${i < idx ? 'bg-blue-600' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function RequestStatus() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = () => {
    publicDocumentRequests.status(token)
      .then((res) => setReq(res.data))
      .catch(() => setError('Request not found.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    if (searchParams.get('paid') === '1') {
      const t = setTimeout(load, 1500); // give the webhook a moment to land
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : error || !req ? (
            <div className="text-center py-8 text-gray-500">{error || 'Request not found.'}</div>
          ) : (
            <>
              <h1 className="text-xl font-bold text-gray-900 mb-1">{REQUEST_TYPE_LABELS[req.request_type]}</h1>
              <p className="text-gray-500 text-sm mb-6">
                {req.building_name}{req.unit_number ? ` · Unit ${req.unit_number}` : ''}
              </p>

              <StageTracker status={req.status} />

              {req.status === 'pending_payment' && (
                <p className="text-sm text-gray-500 text-center">Waiting for payment to complete.</p>
              )}
              {req.status === 'paid' && (
                <p className="text-sm text-gray-500 text-center">
                  Payment received — the strata manager will fulfill your request shortly.
                  This page will update once it's ready.
                </p>
              )}
              {req.status === 'fulfilled' && (
                <div className="text-center">
                  <p className="text-sm text-green-700 mb-4">Your document is ready.</p>
                  <a href={publicDocumentRequests.downloadUrl(token)}
                    className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-colors">
                    Download Document
                  </a>
                </div>
              )}

              <button onClick={load} className="w-full text-xs text-gray-400 hover:text-gray-600 mt-6">
                Refresh status
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
